import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import {
    ModalChrome,
    ModalBody,
    ModalContentTile,
    CenteredLoadingOverlay,
    comparisonFieldSx,
    outlinedActionButtonSx,
    getWindowChromeThemeColor,
    ConfirmDialog,
    confirmDialogBackdropSx,
    confirmDialogButtonSx,
} from "./windowChrome/index.js";

/** Matches Admin modal / GrainWaveRole schema fallback. */
const DEFAULT_GRAIN_WAVE_ROLES = [
    { grainWaveRoleId: 1, name: "Administrator" },
    { grainWaveRoleId: 2, name: "User" },
    { grainWaveRoleId: 3, name: "ReadOnly" },
];

const VIEW_TABS = ["By Site", "By Role", "By User", "User Tracking"];
const FETCH_CONCURRENCY = 5;

const gwHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: "Bearer " + global.gw.token,
});

const gwUrl = () => `${global.ApiUrl}${global.Endpoints.GrainWave}`;

async function runGraphQL(query, variables = {}) {
    const response = await axios({
        url: gwUrl(),
        method: "post",
        headers: gwHeaders(),
        data: { query, variables },
    });
    const gqlErrors = response?.data?.errors;
    if (gqlErrors?.length) {
        console.warn("Sites & Roles GraphQL error:", gqlErrors);
        return null;
    }
    return response?.data?.data ?? null;
}

function parseMutationJson(value) {
    if (value == null) return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function normalizeTrackingRow(row) {
    const recent = Array.isArray(row?.recentLogins) ? row.recentLogins : [];
    return {
        email: row?.email ?? "",
        first: row?.first ?? "",
        last: row?.last ?? "",
        isLoggedIn: !!row?.isLoggedIn,
        sessionExpiresAt: row?.sessionExpiresAt ?? "",
        lastLoginAt: row?.lastLoginAt ?? "",
        loginCount: Number(row?.loginCount) || 0,
        recentLogins: recent.map((entry) => ({
            occurredAt: entry?.occurredAt ?? entry?.OccurredAt ?? "",
            ipAddress: entry?.ipAddress ?? entry?.IpAddress ?? "",
        })),
    };
}

async function mapPool(items, limit, mapper) {
    const results = new Array(items.length);
    let next = 0;
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const index = next++;
            results[index] = await mapper(items[index], index);
        }
    });
    await Promise.all(workers);
    return results;
}

function normalizeSiteRows(rows) {
    const list = Array.isArray(rows) ? rows : [];
    return list
        .map((x) => ({
            customerSiteGuid: String(
                x.customerSiteGuid ?? x.CustomerSiteGuid ?? x.siteId ?? x.SiteId ?? "",
            ),
            siteName: x.siteName ?? x.SiteName ?? "",
            isSelected: !!(x.isSelected ?? x.IsSelected),
        }))
        .filter((x) => x.customerSiteGuid);
}

async function fetchSiteCatalog(email, selectedOnly) {
    const queries = [
        `
        query GetUserSelectedAllSites($email: String!) {
            getUserSelectedAllSites(email: $email) {
                siteId
                siteName
                isSelected
            }
        }
        `,
        `
        query GetUserSelectedAllSites($email: String!) {
            getUserSelectedAllSites(email: $email) {
                customerSiteGuid
                siteName
                isSelected
            }
        }
        `,
    ];
    for (const query of queries) {
        try {
            const data = await runGraphQL(query, { email });
            const rows = data?.getUserSelectedAllSites;
            if (Array.isArray(rows)) {
                const sites = normalizeSiteRows(rows);
                return selectedOnly ? sites.filter((s) => s.isSelected) : sites;
            }
        } catch {
            // try alternate field naming
        }
    }
    return [];
}

async function fetchUserSites(email) {
    return fetchSiteCatalog(email, true);
}

async function fetchUserRoleId(email) {
    try {
        const data = await runGraphQL(
            `query R($t: String!) { fetchUserGrainWaveRoleId(targetUserEmail: $t) }`,
            { t: email },
        );
        const rid = data?.fetchUserGrainWaveRoleId ?? data?.FetchUserGrainWaveRoleId;
        if (rid == null || rid === "") return 2;
        const n = Number(rid);
        return Number.isNaN(n) ? 2 : n;
    } catch {
        return 2;
    }
}

const HISTORY_EVENT_LABELS = {
    Login: "Signed in",
    ForcedLogout: "Logged out by admin",
    Impersonate: "Impersonated",
};

function SectionCard({ title, subtitle, children, text, subtle, border, sectionBg, action }) {
    const heading = (
        <>
            <MuiMaterials.Typography
                sx={{ fontWeight: 600, color: text, fontSize: "14px", mb: subtitle ? 0.25 : 1 }}
            >
                {title}
            </MuiMaterials.Typography>
            {subtitle ? (
                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", mb: 1 }}>
                    {subtitle}
                </MuiMaterials.Typography>
            ) : null}
        </>
    );
    return (
        <MuiMaterials.Box
            sx={{
                backgroundColor: sectionBg,
                border: `1px solid ${border}`,
                borderRadius: "8px",
                p: 1.5,
                mb: 1.5,
            }}
        >
            {action ? (
                <MuiMaterials.Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <MuiMaterials.Box sx={{ flex: 1, minWidth: 0 }}>{heading}</MuiMaterials.Box>
                    {action}
                </MuiMaterials.Box>
            ) : heading}
            {children}
        </MuiMaterials.Box>
    );
}

function UserLine({ primary, secondary, text, subtle, action }) {
    return (
        <MuiMaterials.Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 0.25, sm: 1 },
                py: 0.5,
                borderTop: `1px solid ${getWindowChromeThemeColor("#ececec", "#333333")}`,
            }}
        >
            <MuiMaterials.Typography sx={{ color: text, fontSize: "13px", fontWeight: 500 }}>
                {primary}
            </MuiMaterials.Typography>
            {secondary ? (
                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", flex: 1 }}>
                    {secondary}
                </MuiMaterials.Typography>
            ) : <MuiMaterials.Box sx={{ flex: 1 }} />}
            {action || null}
        </MuiMaterials.Box>
    );
}

const SitesAndRolesModal = ({ isModalOpen, setIsModalOpen }) => {
    const [themeVersion, setThemeVersion] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [allSites, setAllSites] = useState([]);
    const [roles, setRoles] = useState(DEFAULT_GRAIN_WAVE_ROLES);
    const [viewTab, setViewTab] = useState(0);
    const [filterText, setFilterText] = useState("");
    const [tracking, setTracking] = useState([]);
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [trackingError, setTrackingError] = useState(null);
    const [busyEmail, setBusyEmail] = useState(null);
    const [siteBusy, setSiteBusy] = useState(null);
    const [addPick, setAddPick] = useState({});
    const [trackingConfirm, setTrackingConfirm] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    useEffect(() => {
        const onTheme = () => setThemeVersion((v) => v + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    const { text, subtle, border, sectionBg, tileTabBorder } = useMemo(
        () => ({
            text: getWindowChromeThemeColor("#333333", "#f4cc8e"),
            subtle: getWindowChromeThemeColor("#666666", "#a8abb1"),
            border: getWindowChromeThemeColor("#e0e0e0", "#3d3d3d"),
            sectionBg: getWindowChromeThemeColor("#ffffff", "#2d2d2d"),
            tileTabBorder: getWindowChromeThemeColor("#e0e0e0", "#3d3d3d"),
        }),
        [themeVersion],
    );

    const roleNameById = useMemo(() => {
        const map = new Map();
        (roles.length ? roles : DEFAULT_GRAIN_WAVE_ROLES).forEach((r) => {
            map.set(Number(r.grainWaveRoleId), r.name);
        });
        return map;
    }, [roles]);

    const loadAssignments = useCallback(async () => {
        if (!global.gw?.token) return;
        setLoading(true);
        setError(null);
        try {
            const [emailsData, rolesData] = await Promise.all([
                runGraphQL(`query AdminUserEmails { fetchAdminUserEmails { email } }`),
                runGraphQL(`query { fetchGrainWaveRoles { grainWaveRoleId name } }`),
            ]);

            const emailRows = emailsData?.fetchAdminUserEmails;
            const emails = (Array.isArray(emailRows) ? emailRows : [])
                .map((x) => x.email ?? x.Email ?? "")
                .filter((e) => e && e !== "New User");

            const roleRows = rolesData?.fetchGrainWaveRoles;
            const normalizedRoles = (Array.isArray(roleRows) ? roleRows : [])
                .map((x) => ({
                    grainWaveRoleId: x.grainWaveRoleId ?? x.GrainWaveRoleId,
                    name: x.name ?? x.Name,
                }))
                .filter((x) => x.grainWaveRoleId != null && x.name);
            const resolvedRoles =
                normalizedRoles.length > 0 ? normalizedRoles : DEFAULT_GRAIN_WAVE_ROLES;
            setRoles(resolvedRoles);

            const roleLookup = new Map(
                resolvedRoles.map((r) => [Number(r.grainWaveRoleId), r.name]),
            );

            const loaded = await mapPool(emails, FETCH_CONCURRENCY, async (email) => {
                const [roleId, sites] = await Promise.all([
                    fetchUserRoleId(email),
                    fetchUserSites(email),
                ]);
                return {
                    email,
                    grainWaveRoleId: roleId,
                    roleName: roleLookup.get(roleId) || `Role ${roleId}`,
                    sites: sites
                        .map((s) => ({
                            customerSiteGuid: s.customerSiteGuid,
                            siteName: s.siteName || s.customerSiteGuid,
                        }))
                        .sort((a, b) => a.siteName.localeCompare(b.siteName)),
                };
            });

            loaded.sort((a, b) => a.email.localeCompare(b.email));
            setUsers(loaded);

            const catalog = await fetchSiteCatalog(global.gw.email || "", false);
            const known = new Map();
            catalog.forEach((site) => {
                known.set(site.customerSiteGuid.toLowerCase(), {
                    customerSiteGuid: site.customerSiteGuid,
                    siteName: site.siteName || site.customerSiteGuid,
                });
            });
            loaded.forEach((user) => {
                user.sites.forEach((site) => {
                    const key = site.customerSiteGuid.toLowerCase();
                    if (!known.has(key)) known.set(key, site);
                });
            });
            setAllSites([...known.values()].sort((a, b) => a.siteName.localeCompare(b.siteName)));
        } catch (err) {
            console.warn("Sites & Roles load failed:", err);
            setError("Could not load site and role assignments.");
            setUsers([]);
            setAllSites([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTracking = useCallback(async () => {
        if (!global.gw?.token) return;
        setTrackingLoading(true);
        setTrackingError(null);
        try {
            const data = await runGraphQL(`
                query FetchUserTracking {
                    fetchUserTracking {
                        email
                        first
                        last
                        isLoggedIn
                        sessionExpiresAt
                        lastLoginAt
                        loginCount
                        recentLogins { occurredAt ipAddress }
                    }
                }
            `);
            const rows = data?.fetchUserTracking;
            if (!Array.isArray(rows)) {
                setTrackingError("Could not load user tracking.");
                setTracking([]);
                return [];
            }
            const normalized = rows.map(normalizeTrackingRow).filter((row) => row.email);
            setTracking(normalized);
            return normalized;
        } catch (err) {
            console.warn("User tracking load failed:", err);
            setTrackingError("Could not load user tracking.");
            setTracking([]);
            return [];
        } finally {
            setTrackingLoading(false);
        }
    }, []);

    const requestLogout = (email, isLoggedIn) => {
        const self = email.toLowerCase() === String(global.gw.email || "").toLowerCase();
        const message = self
            ? `Log out ${email}? You will be returned to the login screen.`
            : isLoggedIn
                ? `Log out ${email}? They will need to sign in again.`
                : `End any leftover session for ${email}?`;
        setTrackingConfirm({
            kind: "logout",
            email,
            isLoggedIn,
            title: "Log out",
            message,
            confirmLabel: "Log out",
        });
    };

    const logoutTrackedUser = useCallback(async (email) => {
        const self = email.toLowerCase() === String(global.gw.email || "").toLowerCase();
        setBusyEmail(email);
        setTrackingError(null);
        try {
            const data = await runGraphQL(
                `mutation ForceLogout($email: String!) { forceLogoutUser(targetUserEmail: $email) }`,
                { email },
            );
            const result = data?.forceLogoutUser;
            if (result !== "ok") {
                setTrackingError(typeof result === "string" && result ? result : "Could not log that user out.");
                return;
            }
            if (self && !global.readImpersonator()) {
                delete axios.defaults.headers.common["Authorization"];
                global.gw.token = null;
                global.gw.auth = null;
                global.gw.isAuthenticated = false;
                global.clearAuthState();
                window.location.assign("/emailLogin");
                return;
            }
            const rows = await loadTracking();
            const stillIn = (rows || []).find(
                (row) => row.email.toLowerCase() === email.toLowerCase() && row.isLoggedIn,
            );
            if (stillIn) {
                setTrackingError(`${email} is still signed in. Log out did not end their session.`);
            }
        } catch (err) {
            console.warn("Log out failed:", err);
            setTrackingError("Could not log that user out.");
        } finally {
            setBusyEmail(null);
        }
    }, [loadTracking]);

    const requestImpersonate = (row) => {
        const name = [row.first, row.last].filter((part) => part && String(part).trim()).join(" ").trim();
        const who = name ? `${name} (${row.email})` : row.email;
        setTrackingConfirm({
            kind: "impersonate",
            row,
            title: "Impersonate",
            message: `View GrainWave as ${who}? You can return to your account from the bar at the top of the page.`,
            confirmLabel: "Impersonate",
        });
    };

    const closeTrackingConfirm = () => {
        if (busyEmail) return;
        setTrackingConfirm(null);
    };

    const acceptTrackingConfirm = () => {
        const pending = trackingConfirm;
        setTrackingConfirm(null);
        if (!pending) return;
        if (pending.kind === "logout") {
            logoutTrackedUser(pending.email);
            return;
        }
        if (pending.kind === "removeSite" && pending.site) {
            setSiteMembership(pending.email, pending.site, false);
            return;
        }
        if (pending.kind === "deleteUser") {
            deleteUser(pending.email);
            return;
        }
        if (pending.row) impersonateTrackedUser(pending.row);
    };

    const impersonateTrackedUser = useCallback(async (row) => {
        setBusyEmail(row.email);
        setTrackingError(null);
        try {
            const data = await runGraphQL(
                `mutation Impersonate($email: String!) { impersonateUser(targetUserEmail: $email) }`,
                { email: row.email },
            );
            const payload = parseMutationJson(data?.impersonateUser);
            if (!payload?.ok || !payload.token) {
                setTrackingError(payload?.error || "Could not impersonate that user.");
                return;
            }
            global.beginImpersonation({
                token: payload.token,
                email: payload.email || row.email,
                first: payload.first,
                last: payload.last,
                roles: payload.roles,
            });
            window.location.assign("/sites");
        } catch (err) {
            console.warn("Impersonate failed:", err);
            setTrackingError("Could not impersonate that user.");
        } finally {
            setBusyEmail(null);
        }
    }, []);

    const setSiteMembership = useCallback(async (email, site, assigned) => {
        const key = `${site.customerSiteGuid}|${email}`;
        setSiteBusy(key);
        setError(null);
        try {
            const data = await runGraphQL(
                `mutation SetUserSite($email: String!, $siteId: String!, $assigned: Boolean!) {
                    setUserSiteAssignment(targetUserEmail: $email, siteId: $siteId, assigned: $assigned)
                }`,
                { email, siteId: site.customerSiteGuid, assigned },
            );
            const result = data?.setUserSiteAssignment;
            if (result !== "ok") {
                setError(typeof result === "string" && result ? result : "Could not update that site.");
                return;
            }
            const guid = site.customerSiteGuid.toLowerCase();
            setUsers((prev) => prev.map((user) => {
                if (user.email.toLowerCase() !== email.toLowerCase()) return user;
                const already = user.sites.some((row) => row.customerSiteGuid.toLowerCase() === guid);
                let sites = user.sites;
                if (assigned && !already) {
                    sites = [...user.sites, {
                        customerSiteGuid: site.customerSiteGuid,
                        siteName: site.siteName,
                    }].sort((a, b) => a.siteName.localeCompare(b.siteName));
                } else if (!assigned) {
                    sites = user.sites.filter((row) => row.customerSiteGuid.toLowerCase() !== guid);
                }
                return { ...user, sites };
            }));
            if (assigned) {
                setAddPick((prev) => {
                    if (!prev[site.customerSiteGuid]) return prev;
                    const next = { ...prev };
                    delete next[site.customerSiteGuid];
                    return next;
                });
            }
        } catch (err) {
            console.warn("Site membership update failed:", err);
            setError("Could not update that site.");
        } finally {
            setSiteBusy(null);
        }
    }, []);

    const openHistory = useCallback(async (row) => {
        setHistoryFor(row);
        setHistory([]);
        setHistoryError(null);
        setHistoryLoading(true);
        const fallback = row.recentLogins.map((entry) => ({ ...entry, eventKind: "Login", actorEmail: "" }));
        try {
            const data = await runGraphQL(
                `query LoginHistory($email: String!) {
                    fetchUserLoginHistory(targetUserEmail: $email) { occurredAt eventKind actorEmail ipAddress }
                }`,
                { email: row.email },
            );
            const rows = data?.fetchUserLoginHistory;
            if (!Array.isArray(rows)) {
                setHistory(fallback);
                return;
            }
            setHistory(rows.map((entry) => ({
                occurredAt: entry?.occurredAt ?? "",
                eventKind: entry?.eventKind ?? "",
                actorEmail: entry?.actorEmail ?? "",
                ipAddress: entry?.ipAddress ?? "",
            })));
        } catch (err) {
            console.warn("Sign-in history load failed:", err);
            setHistoryError("Could not load the full history. Showing recent sign-ins only.");
            setHistory(fallback);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const closeHistory = () => {
        setHistoryFor(null);
        setHistory([]);
        setHistoryError(null);
    };

    const requestDeleteUser = (user) => {
        const siteCount = user.sites.length;
        setTrackingConfirm({
            kind: "deleteUser",
            email: user.email,
            title: "Remove user",
            message: `Permanently remove ${user.email}? Their account, ${siteCount} site assignment${siteCount === 1 ? "" : "s"}, role, saved views, and sign-in history will be deleted. This cannot be undone.`,
            confirmLabel: "Remove",
        });
    };

    const deleteUser = useCallback(async (email) => {
        setBusyEmail(email);
        setError(null);
        try {
            const data = await runGraphQL(
                `mutation DeleteUser($email: String!) { deleteUser(targetUserEmail: $email) }`,
                { email },
            );
            const result = data?.deleteUser;
            if (result !== "ok") {
                setError(typeof result === "string" && result ? result : "Could not remove that user.");
                return;
            }
            const key = email.toLowerCase();
            setUsers((prev) => prev.filter((user) => user.email.toLowerCase() !== key));
            setTracking((prev) => prev.filter((row) => row.email.toLowerCase() !== key));
        } catch (err) {
            console.warn("Remove user failed:", err);
            setError("Could not remove that user.");
        } finally {
            setBusyEmail(null);
        }
    }, []);

    const requestRemoveFromSite = (email, site) => {
        setTrackingConfirm({
            kind: "removeSite",
            email,
            site,
            title: "Remove user",
            message: `Remove ${email} from ${site.siteName}? They will lose access to this site.`,
            confirmLabel: "Remove",
        });
    };

    useEffect(() => {
        if (!isModalOpen[0]) {
            setFilterText("");
            setViewTab(0);
            setError(null);
            setTrackingError(null);
            setBusyEmail(null);
            setSiteBusy(null);
            setAddPick({});
            setTrackingConfirm(null);
            setHistoryFor(null);
            setHistory([]);
            setHistoryError(null);
            return;
        }
        loadAssignments();
    }, [isModalOpen, loadAssignments]);

    useEffect(() => {
        if (!isModalOpen[0] || viewTab !== 3) return;
        loadTracking();
    }, [isModalOpen, viewTab, loadTracking]);

    const filter = filterText.trim().toLowerCase();
    const impersonating = !!global.readImpersonator();

    const filteredTracking = useMemo(() => {
        if (!filter) return tracking;
        return tracking.filter((row) => {
            if (row.email.toLowerCase().includes(filter)) return true;
            if ((row.first || "").toLowerCase().includes(filter)) return true;
            return (row.last || "").toLowerCase().includes(filter);
        });
    }, [tracking, filter]);

    const filteredUsers = useMemo(() => {
        if (!filter) return users;
        return users.filter((u) => {
            if (u.email.toLowerCase().includes(filter)) return true;
            if ((u.roleName || "").toLowerCase().includes(filter)) return true;
            return u.sites.some(
                (s) =>
                    s.siteName.toLowerCase().includes(filter) ||
                    s.customerSiteGuid.toLowerCase().includes(filter),
            );
        });
    }, [users, filter]);

    const bySite = useMemo(() => {
        const map = new Map();
        allSites.forEach((site) => {
            map.set(site.customerSiteGuid.toLowerCase(), {
                customerSiteGuid: site.customerSiteGuid,
                siteName: site.siteName,
                users: [],
            });
        });
        users.forEach((user) => {
            user.sites.forEach((site) => {
                const key = site.customerSiteGuid.toLowerCase();
                if (!map.has(key)) {
                    map.set(key, {
                        customerSiteGuid: site.customerSiteGuid,
                        siteName: site.siteName,
                        users: [],
                    });
                }
                map.get(key).users.push(user);
            });
        });
        return [...map.values()]
            .map((entry) => {
                const usersOnSite = [...entry.users].sort((a, b) => a.email.localeCompare(b.email));
                const siteHit = !filter
                    || entry.siteName.toLowerCase().includes(filter)
                    || entry.customerSiteGuid.toLowerCase().includes(filter);
                const matchingUsers = filter
                    ? usersOnSite.filter((user) =>
                        user.email.toLowerCase().includes(filter)
                        || (user.roleName || "").toLowerCase().includes(filter))
                    : usersOnSite;
                if (filter && !siteHit && matchingUsers.length === 0) return null;
                return {
                    ...entry,
                    users: usersOnSite,
                    visibleUsers: siteHit ? usersOnSite : matchingUsers,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.siteName.localeCompare(b.siteName));
    }, [allSites, users, filter]);

    const byRole = useMemo(() => {
        const map = new Map();
        (roles.length ? roles : DEFAULT_GRAIN_WAVE_ROLES).forEach((r) => {
            map.set(Number(r.grainWaveRoleId), {
                grainWaveRoleId: Number(r.grainWaveRoleId),
                roleName: r.name,
                users: [],
            });
        });
        filteredUsers.forEach((u) => {
            const id = Number(u.grainWaveRoleId) || 2;
            if (!map.has(id)) {
                map.set(id, {
                    grainWaveRoleId: id,
                    roleName: roleNameById.get(id) || u.roleName || `Role ${id}`,
                    users: [],
                });
            }
            map.get(id).users.push(u);
        });
        return [...map.values()]
            .map((entry) => ({
                ...entry,
                users: [...entry.users].sort((a, b) => a.email.localeCompare(b.email)),
            }))
            .sort((a, b) => a.roleName.localeCompare(b.roleName));
    }, [filteredUsers, roles, roleNameById]);

    const handleClose = () => {
        if (trackingConfirm) {
            setTrackingConfirm(null);
            return;
        }
        if (historyFor) {
            closeHistory();
            return;
        }
        setIsModalOpen([false]);
    };

    const emptyMessage = (label) => (
        <MuiMaterials.Typography sx={{ color: subtle, fontSize: "13px", py: 2, textAlign: "center" }}>
            {label}
        </MuiMaterials.Typography>
    );

    return (
        <>
        <ModalChrome
            open={isModalOpen[0]}
            onClose={handleClose}
            title="Sites & Roles"
            modalProps={trackingConfirm || historyFor ? { disableEnforceFocus: true } : null}
            loadingOverlay={loading ? <CenteredLoadingOverlay /> : null}
            actions={
                <MuiMaterials.Button
                    size="small"
                    variant="outlined"
                    disabled={loading || trackingLoading}
                    onClick={() => (viewTab === 3 ? loadTracking() : loadAssignments())}
                    sx={outlinedActionButtonSx()}
                >
                    Refresh
                </MuiMaterials.Button>
            }
        >
            <ModalBody>
                <MuiMaterials.Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0,
                        marginBottom: 1,
                        borderBottom: `1px solid ${tileTabBorder}`,
                        flexShrink: 0,
                    }}
                >
                    {VIEW_TABS.map((label, i) => (
                        <MuiMaterials.Button
                            key={label}
                            onClick={() => setViewTab(i)}
                            sx={{
                                color: viewTab === i ? text : subtle,
                                borderBottom:
                                    viewTab === i
                                        ? `2px solid ${text}`
                                        : "2px solid transparent",
                                borderRadius: 0,
                                minWidth: "auto",
                                px: 2,
                                py: 1,
                                textTransform: "none",
                            }}
                        >
                            {label}
                        </MuiMaterials.Button>
                    ))}
                </MuiMaterials.Box>

                <MuiMaterials.TextField
                    size="small"
                    label="Filter"
                    placeholder={viewTab === 3 ? "Email or name…" : "Email, site, or role…"}
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    fullWidth
                    sx={{
                        ...comparisonFieldSx(),
                        mb: 1.5,
                        flexShrink: 0,
                        maxWidth: 420,
                    }}
                />

                <ModalContentTile
                    sx={{
                        overflowX: "hidden",
                        overflowY: "auto",
                        overscrollBehavior: "contain",
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    {error ? (
                        <MuiMaterials.Typography sx={{ color: "#c62828", fontSize: "13px", mb: 1 }}>
                            {error}
                        </MuiMaterials.Typography>
                    ) : null}

                    {!loading && viewTab === 0 && (
                        <>
                            {impersonating ? (
                                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", mb: 1 }}>
                                    Return to your account before adding or removing users.
                                </MuiMaterials.Typography>
                            ) : null}
                            {bySite.length === 0
                            ? emptyMessage(filter ? "No sites match this filter." : "No sites found.")
                            : bySite.map((site) => {
                                const assignedEmails = new Set(site.users.map((user) => user.email.toLowerCase()));
                                const available = users.filter((user) => !assignedEmails.has(user.email.toLowerCase()));
                                const picked = addPick[site.customerSiteGuid] || "";
                                const addKey = `${site.customerSiteGuid}|${picked}`;
                                return (
                                    <SectionCard
                                        key={site.customerSiteGuid}
                                        title={site.siteName}
                                        subtitle={`${site.users.length} user${site.users.length === 1 ? "" : "s"}`}
                                        text={text}
                                        subtle={subtle}
                                        border={border}
                                        sectionBg={sectionBg}
                                    >
                                        <MuiMaterials.Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                alignItems: "center",
                                                flexWrap: "wrap",
                                                mb: 1,
                                            }}
                                        >
                                            <MuiMaterials.TextField
                                                select
                                                size="small"
                                                label="Add user"
                                                value={available.some((user) => user.email === picked) ? picked : ""}
                                                disabled={impersonating || available.length === 0 || !!siteBusy}
                                                onChange={(event) => setAddPick((prev) => ({
                                                    ...prev,
                                                    [site.customerSiteGuid]: event.target.value,
                                                }))}
                                                sx={{ ...comparisonFieldSx(), minWidth: 220, flex: "1 1 220px" }}
                                            >
                                                <MuiMaterials.MenuItem value="" disabled>
                                                    {available.length === 0 ? "Everyone is on this site" : "Select a user"}
                                                </MuiMaterials.MenuItem>
                                                {available.map((user) => (
                                                    <MuiMaterials.MenuItem key={user.email} value={user.email}>
                                                        {user.email}
                                                    </MuiMaterials.MenuItem>
                                                ))}
                                            </MuiMaterials.TextField>
                                            <MuiMaterials.Button
                                                size="small"
                                                variant="outlined"
                                                disabled={impersonating || !picked || !!siteBusy || available.length === 0}
                                                onClick={() => setSiteMembership(picked, site, true)}
                                                sx={outlinedActionButtonSx()}
                                            >
                                                {siteBusy === addKey ? "Adding…" : "Add"}
                                            </MuiMaterials.Button>
                                        </MuiMaterials.Box>
                                        {site.visibleUsers.length === 0 ? (
                                            <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                                                No users on this site.
                                            </MuiMaterials.Typography>
                                        ) : site.visibleUsers.map((user) => {
                                            const removeKey = `${site.customerSiteGuid}|${user.email}`;
                                            return (
                                                <UserLine
                                                    key={`${site.customerSiteGuid}-${user.email}`}
                                                    primary={user.email}
                                                    secondary={user.roleName}
                                                    text={text}
                                                    subtle={subtle}
                                                    action={(
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={impersonating || !!siteBusy}
                                                            onClick={() => requestRemoveFromSite(user.email, site)}
                                                            sx={outlinedActionButtonSx()}
                                                        >
                                                            {siteBusy === removeKey ? "Removing…" : "Remove"}
                                                        </MuiMaterials.Button>
                                                    )}
                                                />
                                            );
                                        })}
                                    </SectionCard>
                                );
                            })}
                        </>
                    )}

                    {!loading && viewTab === 1 && (
                        byRole.every((r) => r.users.length === 0)
                            ? emptyMessage(filter ? "No roles match this filter." : "No users found.")
                            : byRole
                                .filter((r) => r.users.length > 0 || !filter)
                                .map((role) => (
                                    <SectionCard
                                        key={role.grainWaveRoleId}
                                        title={role.roleName}
                                        subtitle={`${role.users.length} user${role.users.length === 1 ? "" : "s"}`}
                                        text={text}
                                        subtle={subtle}
                                        border={border}
                                        sectionBg={sectionBg}
                                    >
                                        {role.users.length === 0
                                            ? (
                                                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                                                    No users with this role.
                                                </MuiMaterials.Typography>
                                            )
                                            : role.users.map((u) => (
                                                <UserLine
                                                    key={`${role.grainWaveRoleId}-${u.email}`}
                                                    primary={u.email}
                                                    secondary={
                                                        u.sites.length
                                                            ? u.sites.map((s) => s.siteName).join(", ")
                                                            : "No sites"
                                                    }
                                                    text={text}
                                                    subtle={subtle}
                                                />
                                            ))}
                                    </SectionCard>
                                ))
                    )}

                    {!loading && viewTab === 2 && (
                        filteredUsers.length === 0
                            ? emptyMessage(filter ? "No users match this filter." : "No users found.")
                            : filteredUsers.map((u) => {
                                const self = u.email.toLowerCase() === String(global.gw.email || "").toLowerCase();
                                return (
                                <SectionCard
                                    key={u.email}
                                    title={u.email}
                                    subtitle={u.roleName}
                                    text={text}
                                    subtle={subtle}
                                    border={border}
                                    sectionBg={sectionBg}
                                    action={(
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            disabled={self || impersonating || !!busyEmail}
                                            title={self ? "You cannot remove your own account." : undefined}
                                            onClick={() => requestDeleteUser(u)}
                                            sx={outlinedActionButtonSx()}
                                        >
                                            {busyEmail === u.email ? "Removing…" : "Remove"}
                                        </MuiMaterials.Button>
                                    )}
                                >
                                    {u.sites.length === 0
                                        ? (
                                            <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                                                No sites assigned.
                                            </MuiMaterials.Typography>
                                        )
                                        : u.sites.map((s) => (
                                            <UserLine
                                                key={`${u.email}-${s.customerSiteGuid}`}
                                                primary={s.siteName}
                                                text={text}
                                                subtle={subtle}
                                            />
                                        ))}
                                </SectionCard>
                                );
                            })
                    )}

                    {!loading && viewTab === 3 && (
                        <>
                            <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", mb: 1.25 }}>
                                Signed in means they still have a session. Sign-in times are recorded from the next login after tracking is deployed.
                                Logging someone out ends that session immediately.
                            </MuiMaterials.Typography>
                            {trackingError ? (
                                <MuiMaterials.Typography sx={{ color: "#c62828", fontSize: "13px", mb: 1 }}>
                                    {trackingError}
                                </MuiMaterials.Typography>
                            ) : null}
                            {impersonating ? (
                                <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", mb: 1 }}>
                                    Return to your account before logging someone out or impersonating another user.
                                </MuiMaterials.Typography>
                            ) : null}
                            {trackingLoading
                                ? emptyMessage("Loading user tracking…")
                                : filteredTracking.length === 0
                                    ? emptyMessage(filter ? "No users match this filter." : "No users found.")
                                    : (
                                        <>
                                            <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px", mb: 1 }}>
                                                {filteredTracking.filter((row) => row.isLoggedIn).length} signed in
                                            </MuiMaterials.Typography>
                                            {filteredTracking.map((row) => {
                                                const name = [row.first, row.last].filter((part) => part && String(part).trim()).join(" ").trim();
                                                const self = row.email.toLowerCase() === String(global.gw.email || "").toLowerCase();
                                                const statusColor = row.isLoggedIn
                                                    ? getWindowChromeThemeColor("#2e7d32", "#66bb6a")
                                                    : getWindowChromeThemeColor("#c62828", "#ef5350");
                                                const statusLabel = row.isLoggedIn
                                                    ? (row.sessionExpiresAt ? `Signed in until ${row.sessionExpiresAt}` : "Signed in")
                                                    : "Signed out";
                                                const countLabel = row.loginCount
                                                    ? `${row.loginCount} sign-in${row.loginCount === 1 ? "" : "s"}`
                                                    : "No sign-ins recorded";
                                                const busy = busyEmail === row.email;
                                                return (
                                                    <SectionCard
                                                        key={row.email}
                                                        title={row.email}
                                                        subtitle={[name, countLabel].filter(Boolean).join(" · ")}
                                                        text={text}
                                                        subtle={subtle}
                                                        border={border}
                                                        sectionBg={sectionBg}
                                                    >
                                                        <MuiMaterials.Typography
                                                            sx={{ color: statusColor, fontSize: "13px", fontWeight: 600, mb: 1 }}
                                                        >
                                                            {statusLabel}
                                                        </MuiMaterials.Typography>
                                                        <MuiMaterials.Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                                            <MuiMaterials.Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={!row.isLoggedIn || busy || impersonating}
                                                                onClick={() => requestLogout(row.email, row.isLoggedIn)}
                                                                sx={outlinedActionButtonSx()}
                                                            >
                                                                Log out
                                                            </MuiMaterials.Button>
                                                            <MuiMaterials.Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={self || busy || impersonating}
                                                                onClick={() => requestImpersonate(row)}
                                                                sx={outlinedActionButtonSx()}
                                                            >
                                                                Impersonate
                                                            </MuiMaterials.Button>
                                                            <MuiMaterials.Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={busy}
                                                                onClick={() => openHistory(row)}
                                                                sx={outlinedActionButtonSx()}
                                                            >
                                                                History
                                                            </MuiMaterials.Button>
                                                        </MuiMaterials.Box>
                                                        <MuiMaterials.Typography sx={{ color: subtle, fontSize: "12px" }}>
                                                            {row.lastLoginAt
                                                                ? `Last sign-in ${row.lastLoginAt}`
                                                                : row.isLoggedIn
                                                                    ? "This session started before sign-in times were recorded."
                                                                    : "No sign-in times yet."}
                                                        </MuiMaterials.Typography>
                                                    </SectionCard>
                                                );
                                            })}
                                        </>
                                    )}
                        </>
                    )}
                </ModalContentTile>
            </ModalBody>
        </ModalChrome>
        <ConfirmDialog
            open={!!trackingConfirm}
            onClose={closeTrackingConfirm}
            title={trackingConfirm?.title || ""}
            titleId="user-tracking-confirm-title"
            message={trackingConfirm?.message || null}
            modalProps={{
                sx: { ...confirmDialogBackdropSx(), zIndex: 1600 },
            }}
            actions={(
                <>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={closeTrackingConfirm}
                    >
                        Cancel
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={confirmDialogButtonSx()}
                        onClick={acceptTrackingConfirm}
                    >
                        {trackingConfirm?.confirmLabel || "OK"}
                    </MuiMaterials.Button>
                </>
            )}
        />
        <ConfirmDialog
            open={!!historyFor}
            onClose={closeHistory}
            title={historyFor ? `Sign-in history: ${historyFor.email}` : ""}
            titleId="user-tracking-history-title"
            modalProps={{
                sx: { ...confirmDialogBackdropSx(), zIndex: 1500 },
            }}
            actions={(
                <MuiMaterials.Button
                    size="small"
                    variant="outlined"
                    sx={confirmDialogButtonSx()}
                    onClick={closeHistory}
                >
                    Close
                </MuiMaterials.Button>
            )}
        >
            <MuiMaterials.Box
                sx={{
                    mt: 1.5,
                    width: "min(520px, 80vw)",
                    maxHeight: "60vh",
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                }}
            >
                {historyError ? (
                    <MuiMaterials.Typography sx={{ color: "#c62828", fontSize: "13px", mb: 1 }}>
                        {historyError}
                    </MuiMaterials.Typography>
                ) : null}
                {historyLoading
                    ? emptyMessage("Loading history…")
                    : history.length === 0
                        ? emptyMessage("No sign-ins recorded.")
                        : history.map((entry, index) => {
                            const label = HISTORY_EVENT_LABELS[entry.eventKind] || entry.eventKind || "Signed in";
                            const detail = [
                                label,
                                entry.actorEmail && entry.eventKind !== "Login" ? `by ${entry.actorEmail}` : "",
                                entry.ipAddress,
                            ].filter(Boolean).join(" · ");
                            return (
                                <UserLine
                                    key={`${index}-${entry.occurredAt}`}
                                    primary={entry.occurredAt || "Unknown time"}
                                    secondary={detail}
                                    text={text}
                                    subtle={subtle}
                                />
                            );
                        })}
            </MuiMaterials.Box>
        </ConfirmDialog>
        </>
    );
};

export default SitesAndRolesModal;
